'use client'

import * as React from 'react'

import { cn } from '../../lib/utils'

/**
 * v5 SegmentedToggle — pill-chromed two/three-option switcher.
 *
 * Use when there's a small fixed set of mutually exclusive options
 * and you want both options visible at once (vs. a Switch which
 * hides the alternative behind on/off). Common cases: a view-mode
 * toggle in a Panel header (Week ↔ Calendar, Games ↔ Templates), or
 * a small role / status filter where there are 2–4 choices.
 *
 *   <SegmentedToggle
 *     options={[
 *       { value: 'week', label: 'Week', icon: <Columns3 /> },
 *       { value: 'calendar', label: 'Calendar', icon: <CalendarDays /> },
 *     ]}
 *     value={view}
 *     onChange={setView}
 *   />
 *
 * The active pill is a single absolute-positioned indicator that
 * slides between options on change — visually identical regardless
 * of label width. Movement uses `--ac-dur-base` + `--ac-ease-spring`
 * for a subtle bounce; the white pill itself fades in once measured
 * so SSR doesn't show a misplaced indicator on the first paint.
 *
 * For richer single/multi value selection use <Combobox />; for
 * route-aware view switching use <Tabs /> (mint-underline). For a
 * binary on/off setting (e.g. notifications), prefer <Switch />.
 */

export interface SegmentedToggleOption<V extends string = string> {
  value: V
  label: React.ReactNode
  /** Leading icon, sized 14–16 px works best. */
  icon?: React.ReactNode
  disabled?: boolean
}

export interface SegmentedToggleProps<V extends string = string> {
  options: readonly SegmentedToggleOption<V>[]
  value: V
  onChange: (value: V) => void
  /** ARIA label for the group; defaults to a generic. */
  ariaLabel?: string
  className?: string
  /** Sizing — defaults to `sm` (h-7 pills). `md` bumps to h-9. */
  size?: 'sm' | 'md'
}

export function SegmentedToggle<V extends string = string>({
  options,
  value,
  onChange,
  ariaLabel = 'Toggle',
  className,
  size = 'sm',
}: SegmentedToggleProps<V>) {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const buttonRefs = React.useRef<Array<HTMLButtonElement | null>>([])
  const [indicator, setIndicator] = React.useState<{
    x: number
    width: number
    ready: boolean
  }>({ x: 0, width: 0, ready: false })

  // Re-measure whenever the active option, the option set, or the
  // viewport changes so the indicator stays glued to its button.
  React.useLayoutEffect(() => {
    const measure = () => {
      const idx = options.findIndex((o) => o.value === value)
      const btn = buttonRefs.current[idx]
      const container = containerRef.current
      if (!btn || !container) return
      const containerRect = container.getBoundingClientRect()
      const btnRect = btn.getBoundingClientRect()
      setIndicator({
        x: btnRect.left - containerRect.left,
        width: btnRect.width,
        ready: true,
      })
    }
    measure()
    if (typeof window === 'undefined') return
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [value, options])

  const pillHeight = size === 'sm' ? 'h-7' : 'h-9'
  const padX = size === 'sm' ? 'px-3' : 'px-4'
  const textSize = size === 'sm' ? 'text-[11px]' : 'text-xs'

  return (
    <div
      ref={containerRef}
      role="group"
      aria-label={ariaLabel}
      className={cn(
        'relative inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-[hsl(var(--ac-dark-primary)/0.7)] backdrop-blur-md px-1.5 py-1.5',
        className,
      )}
    >
      {/* Sliding indicator — single white pill that animates between
          options. Positioned with translateX so the browser can
          GPU-composite the motion. */}
      <span
        aria-hidden
        className={cn(
          'absolute top-1.5 bottom-1.5 left-0 rounded-full bg-white',
          '[box-shadow:0_0_25px_hsl(var(--ac-mint-primary))]',
        )}
        style={{
          transform: `translateX(${indicator.x}px)`,
          width: indicator.width,
          opacity: indicator.ready ? 1 : 0,
          transition:
            'transform var(--ac-dur-base) var(--ac-ease-spring), width var(--ac-dur-base) var(--ac-ease-spring), opacity var(--ac-dur-fast) var(--ac-ease-out)',
        }}
      />

      {options.map((opt, i) => {
        const active = value === opt.value
        return (
          <button
            key={opt.value}
            ref={(el) => {
              buttonRefs.current[i] = el
            }}
            type="button"
            aria-pressed={active}
            disabled={opt.disabled}
            onClick={() => {
              if (!opt.disabled) onChange(opt.value)
            }}
            className={cn(
              // z-10 keeps the label above the sliding indicator.
              'relative z-10 inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full font-semibold',
              pillHeight,
              padX,
              textSize,
              active
                ? 'text-[hsl(var(--ac-text-dark))]'
                : 'text-[hsl(var(--ac-text-light))] hover:text-[hsl(var(--ac-mint-primary))]',
              opt.disabled && 'opacity-50 cursor-not-allowed',
              // Color transitions on the label catch up with the
              // sliding pill so the text doesn't flash dark before
              // the indicator arrives.
              'transition-colors duration-[var(--ac-dur-base)] ease-[var(--ac-ease-out)]',
            )}
          >
            {opt.icon && <span className="inline-flex">{opt.icon}</span>}
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}
